// Copyright 2018 Sorama B.V.

export interface CSVHeader {
    key: string | number;
    label: string | number;
}

export class CSVWriter {
    private columnCount: number;
    private columnKeys: (string | number)[];

    private csv: string;
    private seperator = ",";
    private numberFormat: Intl.NumberFormat;

    private whitespaceRegex = /\s/;

    constructor(columnHeaders: CSVHeader[], invariantLocale = true) {
        this.determineLocale(invariantLocale);

        // Determine column keys
        let keys: (string | number)[];
        const validKeyRegex = /\w+/;

        let modeIsKeys: boolean = undefined;

        const headersLabels = <(string | number)[]>[];
        for (const column of columnHeaders) {
            let key: string | number;
            let label: string | number;

            if (column.key !== undefined && column.key !== null) {
                if (modeIsKeys === false)
                    throw new Error("CSVWriter does not support mixing of simple and key/value modes");

                const keyLabel = <CSVHeader>column;

                modeIsKeys = true;
                key = keyLabel.key;
                label = keyLabel.label;
            } else {
                if (modeIsKeys === true)
                    throw new Error("CSVWriter does not support mixing of simple and key/value modes");

                let label = column;

                modeIsKeys = false;
                label = label;
            }

            // Make sure the key does not contain invalid characters
            let keyValid = false;
            if (key === undefined || key === null || typeof key == "number") {
                keyValid = true;
            } else {
                const regexResult = validKeyRegex.exec(<string>key);
                if (regexResult.length == 1) {
                    keyValid = regexResult[0] == key;
                }
            }

            if (!keyValid) {
                throw new Error("Invalid header key for csv exporter '" + key + "'");
            }

            if (key != undefined && key != null) {
                if (!keys) keys = [];

                keys.push(key);
            }

            headersLabels.push(label);
        }

        this.columnKeys = keys;
        this.columnCount = headersLabels.length;

        // Generate CSV for headers
        this.csv = this.toCSVLine(headersLabels);
    }

    public writeLine(values: (string | number)[] | { [key: string]: string | number }) {
        if (values instanceof Array) {
            const v = <string[]>values;
            if (v.length != this.columnCount)
                throw new Error("Number of columns provided does not match CSVWriter configuration");

            this.csv += this.toCSVLine(v);
        } else if (!this.columnKeys) {
            throw new Error("CSVWriter is not configured for key/value pair writing");
        } else {
            const v = <{ [key: string]: string | number }>values;
            const line = <(string | number)[]>[];
            for (const columnKey of this.columnKeys) {
                line.push(v[columnKey]);
            }

            this.csv += this.toCSVLine(line);
        }
    }

    public getCSV(): string {
        return this.csv;
    }

    private determineLocale(invariantLocale: boolean): void {
        if (invariantLocale) {
            this.seperator = ",";
            this.numberFormat = new Intl.NumberFormat("en-US", <Intl.NumberFormatOptions>{ useGrouping: false });
        } else {
            // Test which seperator is used
            const test = (0.1).toString();
            this.seperator = test.indexOf(",") != -1 ? ";" : ",";
            this.numberFormat = new Intl.NumberFormat(undefined, <Intl.NumberFormatOptions>{ useGrouping: false });
        }
    }

    private toCSVLine(values: (string | number)[]): string {
        let line = "";
        for (let i = 0; i < values.length - 1; i++) {
            line += this.toString(values[i]) + this.seperator;
        }

        line += values.length > 0 ? this.toString(values[values.length - 1]) : "";
        line += "\n";

        return line;
    }

    private toString(value: string | number): string {
        if (value === null || value === undefined) return "";

        const str =
            typeof value === "string"
                ? <string>value
                : Number.isFinite(value) && !Number.isNaN(value)
                  ? this.numberFormat.format(<number>value)
                  : "";
        return this.escapeCSVEntry(str);
    }

    private escapeCSVEntry(value: string): string {
        if (!value) return value;

        // Escape any quotes
        // eslint-disable-next-line quotes
        let str = value.replace('"', '""');

        // Check if the entry contains whitespace
        if (this.whitespaceRegex.test(str)) {
            // Surround with quotes
            // eslint-disable-next-line quotes
            str = '"' + str + '"';
        }

        return str;
    }
}
